'use client';

import Image from 'next/image';
import { getThemeBackgroundClass } from '@/lib/theme';
import { trackInteraction } from '@/lib/analytics';
import {
  DragEvent,
  FormEvent,
  KeyboardEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import WaveDivider from './WaveDivider';
import { useDemoSession } from '@/contexts/DemoSessionProvider';
import type {
  ILineAction,
  ILineDemoHistoryEntry,
  TLineDemoMessage,
} from '@/interfaces/lineDemo';
import LineFlexPreview from './LineFlexPreview';
import { CHAT_CHANNELS, type TChatChannel } from '@/lib/chat-channels';

interface LineChatExperienceProps {
  language: 'en' | 'zh';
  businessName?: string;
  /** Messaging channel the demo is styled as. Same conversation engine for all. */
  channel?: TChatChannel;
}

interface ChatMessage {
  id: number;
  role: 'assistant' | 'user';
  text?: string;
  images?: string[];
  imageCount?: number;
  flex?: Extract<TLineDemoMessage, { type: 'flex' }>['contents'];
  quickReplies?: { label: string; action: ILineAction }[];
}

interface StoredChatHistory {
  agentSessionUuid: string;
  messages: ChatMessage[];
}

interface PendingImage {
  id: number;
  name: string;
  dataUrl: string;
  mediaType: string;
  data: string;
}

const MAX_IMAGES = 3;
const MAX_IMAGE_BASE64_CHARS = 8_000_000;
const MAX_IMAGE_TOTAL_BASE64_CHARS = 9_000_000;
const MAX_STORED_MESSAGES = 100;
const MAX_STORED_HISTORY_CHARS = 1_000_000;
// Per channel, so a visitor switching skins does not inherit another skin's thread.
const chatHistoryStorageKey = (channel: TChatChannel) =>
  `demo_${channel}_chat_history_v1`;
const IMAGE_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
]);

function readImageFile(file: File): Promise<Omit<PendingImage, 'id'>> {
  return new Promise((resolve, reject) => {
    if (!IMAGE_TYPES.has(file.type)) {
      reject(new Error('unsupported-image'));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('unreadable-image'));
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('unreadable-image'));
        return;
      }
      const match = reader.result.match(
        /^data:(image\/(?:png|jpeg|webp|gif));base64,(.+)$/
      );
      if (!match || match[2].length > MAX_IMAGE_BASE64_CHARS) {
        reject(new Error('invalid-image'));
        return;
      }
      resolve({
        name: file.name,
        dataUrl: reader.result,
        mediaType: match[1],
        data: match[2],
      });
    };
    reader.readAsDataURL(file);
  });
}

function imageFromDataUrl(
  dataUrl: string,
  name: string
): Omit<PendingImage, 'id'> | null {
  const match = dataUrl.match(
    /^data:(image\/(?:png|jpeg|webp|gif));base64,(.+)$/
  );
  if (!match || match[2].length > MAX_IMAGE_BASE64_CHARS) return null;
  return { name, dataUrl, mediaType: match[1], data: match[2] };
}

function readStoredChatHistory(
  storageKey: string,
  locationId: number
): StoredChatHistory | null {
  try {
    const raw = localStorage.getItem(`${storageKey}:${locationId}`);
    if (!raw || raw.length > MAX_STORED_HISTORY_CHARS) return null;
    const value = JSON.parse(raw) as Partial<StoredChatHistory>;
    if (
      typeof value.agentSessionUuid !== 'string' ||
      !Array.isArray(value.messages)
    ) {
      return null;
    }
    const messages = value.messages.filter(
      (message): message is ChatMessage =>
        !!message &&
        Number.isSafeInteger(message.id) &&
        (message.role === 'assistant' || message.role === 'user')
    );
    if (messages.length !== value.messages.length) return null;
    return {
      agentSessionUuid: value.agentSessionUuid,
      messages: messages.slice(-MAX_STORED_MESSAGES),
    };
  } catch {
    return null;
  }
}

function storableMessages(messages: ChatMessage[]): ChatMessage[] {
  return messages.slice(-MAX_STORED_MESSAGES).map(message => {
    const images = message.images?.filter(
      image => !image.startsWith('data:') && !image.startsWith('blob:')
    );
    const imageCount = message.imageCount ?? message.images?.length;
    return {
      ...message,
      ...(images?.length ? { images } : { images: undefined }),
      ...(imageCount ? { imageCount } : {}),
    };
  });
}

const copy = {
  en: {
    eyebrow: 'INTERACTIVE PREVIEW',
    title: 'Your customers ask on LINE. Obie answers and books.',
    description:
      'Try the conversation yourself. Ask about an opening, a service or an existing booking and see how Obie keeps the customer moving.',
    features: [
      {
        title: 'Books, moves and cancels',
        text: 'Reads the live schedule, offers real openings and confirms the next step.',
        icon: 'calendar',
      },
      {
        title: 'Knows your services',
        text: 'Answers prices, durations, staff, opening hours and business policies.',
        icon: 'tag',
      },
      {
        title: 'Hands over when it should',
        text: 'Unusual requests can move to a person with the conversation attached.',
        icon: 'hand',
      },
    ],
    tryOne: 'Try one of these',
    prompts: [
      'Any openings on Saturday?',
      'How much is a massage?',
      'Move my Thursday booking',
      'What is the cancellation policy?',
    ],
    business: 'Omcean Booking Demo',
    account: 'Official account · Obie replies instantly',
    today: 'Today',
    greeting:
      "Welcome to the Omcean Booking demo! 👋 I'm Obie. I can help you discover services, find the perfect time, or manage an existing booking. What can I help you with today?",
    greetingActions: [
      '🔎 Find an opening',
      '✨ Services & prices',
      '📅 Manage my booking',
    ],
    placeholder: 'Message',
    reset: 'Reset conversation',
    resetPreview: 'Restart preview',
    typing: 'Obie is typing…',
    sending: 'Obie is checking…',
    camera: 'Camera',
    photos: 'Photos',
    dropPhotos: 'Drop photos here',
    removePhoto: 'Remove photo',
    photoLimit: 'You can send up to 3 photos at once.',
    photoError: 'Choose a PNG, JPEG, WebP or GIF image up to about 6 MB.',
    photoTotalError:
      'Those photos are too large to send together. Remove one or choose smaller photos.',
    emoji: 'Emoji',
    emojiRecent: 'Frequently used',
    close: 'Close camera',
    capture: 'Take photo',
    cameraError:
      'Camera access is unavailable. You can choose a photo instead.',
    photoReply:
      'Thanks for the photo. Tell me what you would like me to check, and I’ll help with the next step.',
    photoAlt: 'Photo shared in the conversation',
    caption:
      'Live answers from Obie using the same Demoapt customer and booking data shown in the booking app below.',
    replyError:
      'Sorry, I could not answer that just now. Please try again in a moment.',
  },
  zh: {
    eyebrow: '互動預覽',
    title: '顧客在 LINE 提問，Obie 立即回答並協助預約',
    description:
      '親自試試這段對話。詢問空位、服務或現有預約，看看 Obie 如何引導顧客完成下一步。',
    features: [
      {
        title: '預約、改期與取消',
        text: '讀取即時排程、提供可選時段，並確認下一步。',
        icon: 'calendar',
      },
      {
        title: '了解您的服務',
        text: '回答價格、時長、員工、營業時間與商家政策。',
        icon: 'tag',
      },
      {
        title: '需要時交由真人處理',
        text: '特殊問題可連同完整對話交給真人接手。',
        icon: 'hand',
      },
    ],
    tryOne: '試試這些問題',
    prompts: [
      '週六下午有空位嗎？',
      '按摩服務多少錢？',
      '我想更改週四的預約',
      '取消預約的規定是什麼？',
    ],
    business: 'Omcean Booking 體驗店',
    account: '官方帳號 · Obie 即時回覆',
    today: '今天',
    greeting:
      '歡迎來到 Omcean Booking 體驗店！👋 我是 Obie，可以幫您探索服務、找到合適時段，或管理現有預約。今天想先做什麼呢？',
    greetingActions: ['🔎 查詢空位', '✨ 服務與價格', '📅 管理我的預約'],
    placeholder: '輸入訊息',
    reset: '重新開始對話',
    resetPreview: '重新播放',
    typing: 'Obie 正在輸入⋯',
    sending: 'Obie 正在查詢⋯',
    camera: '相機',
    photos: '照片',
    dropPhotos: '將照片拖曳到這裡',
    removePhoto: '移除照片',
    photoLimit: '每次最多可傳送 3 張照片。',
    photoError: '請選擇約 6 MB 以下的 PNG、JPEG、WebP 或 GIF 圖片。',
    photoTotalError: '這些照片合計容量過大，請移除一張或選擇較小的照片。',
    emoji: '表情符號',
    emojiRecent: '常用表情',
    close: '關閉相機',
    capture: '拍照',
    cameraError: '目前無法使用相機，您可以改為選擇照片。',
    photoReply: '謝謝您的照片。請告訴我想查詢什麼，我會協助您進行下一步。',
    photoAlt: '對話中分享的照片',
    caption: 'Obie 使用與下方預約系統相同的 Demoapt 顧客及預約資料即時回覆。',
    replyError: '抱歉，我剛才無法完成回覆，請稍後再試一次。',
  },
};

function featureIcon(icon: string) {
  if (icon === 'calendar') {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect
          x="3.5"
          y="5"
          width="17"
          height="15.5"
          rx="2.5"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M7.5 3.5v3M16.5 3.5v3M3.5 9h17"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M8 13h2M14 13h2M8 17h2M14 17h2"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  if (icon === 'tag') {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M4 5.5v5.7L12.8 20a2 2 0 0 0 2.8 0l4.4-4.4a2 2 0 0 0 0-2.8L11.2 4H5.5A1.5 1.5 0 0 0 4 5.5Z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <circle cx="8.2" cy="8.2" r="1.3" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M8.2 11.5V6.8a1.4 1.4 0 1 1 2.8 0v3.4-5a1.4 1.4 0 1 1 2.8 0v5-4.1a1.4 1.4 0 1 1 2.8 0v5.4-2.8a1.4 1.4 0 1 1 2.8 0v5.5c0 4-2.8 6.8-6.8 6.8h-.8c-2.5 0-4.2-1.2-5.5-3.3l-2.1-3.5a1.6 1.6 0 0 1 2.5-1.9l1.5 1.4v-2.2Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const emojis = [
  '😊',
  '😂',
  '🥰',
  '😍',
  '🤗',
  '👍',
  '👏',
  '🙏',
  '❤️',
  '🎉',
  '✨',
  '👋',
  '☀️',
  '🌸',
  '💪',
  '🙌',
];

function photoCountLabel(count: number, language: 'en' | 'zh'): string {
  if (language === 'zh') return `${count} 張照片`;
  return count === 1 ? '1 Photo' : `${count} photos`;
}

function quickRepliesForMessage(message: TLineDemoMessage) {
  if (!Array.isArray(message.quickReply?.items)) return undefined;
  return message.quickReply.items
    .map(item => ({
      label: item.action.label || item.action.text || '',
      action: item.action,
    }))
    .filter(reply => reply.label);
}

type LineChatCopy = (typeof copy)[keyof typeof copy];

function greetingMessage(
  text: LineChatCopy,
  nextId: MutableRefObject<number>
): ChatMessage {
  return {
    id: nextId.current++,
    role: 'assistant',
    text: text.greeting,
    quickReplies: text.greetingActions.map(action => ({
      label: action,
      action: { type: 'message', label: action, text: action },
    })),
  };
}

function chatMessagesFromHistory(
  history: ILineDemoHistoryEntry[],
  text: LineChatCopy,
  nextId: MutableRefObject<number>
): ChatMessage[] {
  const restored: ChatMessage[] = [greetingMessage(text, nextId)];
  for (const entry of history) {
    if (entry.agent_message_role === 'user') {
      for (const message of restored) message.quickReplies = undefined;
      restored.push({
        id: nextId.current++,
        role: 'user',
        ...(entry.text ? { text: entry.text } : {}),
        ...(entry.images?.length
          ? { images: entry.images, imageCount: entry.images.length }
          : {}),
      });
      continue;
    }
    if (entry.messages?.length) {
      restored.push(...chatMessagesFromLine(entry.messages, nextId));
    }
  }
  return restored;
}

function chatMessagesFromLine(
  lineMessages: TLineDemoMessage[],
  nextId: MutableRefObject<number>
): ChatMessage[] {
  return lineMessages.map(message => {
    const base = {
      id: nextId.current++,
      role: 'assistant' as const,
      quickReplies: quickRepliesForMessage(message),
    };
    if (message.type === 'text') return { ...base, text: message.text };
    if (message.type === 'image') {
      return {
        ...base,
        images: [message.previewImageUrl],
        imageCount: 1,
      };
    }
    return { ...base, flex: message.contents };
  });
}

export default function LineChatExperience({
  language,
  businessName,
  channel = 'line',
}: LineChatExperienceProps) {
  const { locationId } = useDemoSession();
  return (
    <LineChatExperienceContent
      key={`${channel}-${locationId}`}
      language={language}
      businessName={businessName}
      channel={channel}
      locationId={locationId}
    />
  );
}

function LineChatExperienceContent({
  language,
  businessName,
  channel = 'line',
  locationId,
}: LineChatExperienceProps & { locationId: number }) {
  const skin = CHAT_CHANNELS[channel];
  const c = skin.colors;
  const storageKey = chatHistoryStorageKey(channel);
  const text = useMemo(() => {
    const channelCopy = skin.copy[language];
    const base = {
      ...copy[language],
      eyebrow: channelCopy.eyebrow,
      title: channelCopy.title,
      account: channelCopy.account,
    };
    if (!businessName) return base;
    return {
      ...base,
      business: businessName,
      greeting:
        language === 'en'
          ? `Welcome to ${businessName}! 👋 I'm Obie. Ask me about services, available times or your bookings at this demo business.`
          : `歡迎來到 ${businessName}！👋 我是 Obie，可以協助查詢這間示範商家的服務、可預約時段與您的預約。`,
      prompts: [
        base.prompts[0],
        language === 'en'
          ? 'What services can I book here?'
          : '這裡有哪些服務可以預約？',
        ...base.prompts.slice(2),
      ],
      caption:
        language === 'en'
          ? `Obie uses the same demo customer and booking data as the ${businessName} booking app above.`
          : `Obie 使用與上方 ${businessName} 預約系統相同的示範顧客與預約資料。`,
    };
  }, [businessName, language, skin]);
  const { agentSessionUuid, lineHistory, ensureSession, sendLineMessage } =
    useDemoSession();
  const backgroundClasses = `${getThemeBackgroundClass(skin.theme, 'secondary')} ${skin.sectionGradient}`;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const [isReplying, setIsReplying] = useState(false);
  const [isDraggingImages, setIsDraggingImages] = useState(false);
  const [isHistoryReady, setIsHistoryReady] = useState(false);
  const [hasWelcomed, setHasWelcomed] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const nextId = useRef(1);
  const nextImageId = useRef(1);
  const dragDepth = useRef(0);
  const pendingReply = useRef<number | null>(null);
  const serverHistoryApplied = useRef(false);
  const sectionRef = useRef<HTMLElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cameraStream = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (isHistoryReady) return;
    const stored = readStoredChatHistory(storageKey, locationId);
    if (!stored) {
      setIsHistoryReady(true);
      return;
    }

    let cancelled = false;
    const restore = () => {
      if (cancelled) return;
      setMessages(stored.messages);
      nextId.current =
        Math.max(0, ...stored.messages.map(message => message.id)) + 1;
      setHasWelcomed(stored.messages.length > 0);
    };

    void ensureSession()
      .then(session => {
        if (session.history.length > 0) {
          nextId.current = 1;
          setMessages(chatMessagesFromHistory(session.history, text, nextId));
          setHasWelcomed(true);
          serverHistoryApplied.current = true;
        } else if (session.agent_session_uuid === stored.agentSessionUuid) {
          restore();
        } else {
          localStorage.removeItem(`${storageKey}:${locationId}`);
        }
      })
      .catch(restore)
      .finally(() => {
        if (!cancelled) setIsHistoryReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [ensureSession, isHistoryReady, locationId, storageKey, text]);

  useEffect(() => {
    if (!lineHistory?.length || serverHistoryApplied.current) return;
    nextId.current = 1;
    setMessages(chatMessagesFromHistory(lineHistory, text, nextId));
    setHasWelcomed(true);
    setIsHistoryReady(true);
    serverHistoryApplied.current = true;
  }, [lineHistory, text]);

  useEffect(() => {
    if (!isHistoryReady || !agentSessionUuid) return;
    try {
      if (messages.length === 0) {
        localStorage.removeItem(`${storageKey}:${locationId}`);
        return;
      }
      const serialized = JSON.stringify({
        agentSessionUuid,
        messages: storableMessages(messages),
      } satisfies StoredChatHistory);
      if (serialized.length <= MAX_STORED_HISTORY_CHARS) {
        localStorage.setItem(`${storageKey}:${locationId}`, serialized);
      }
    } catch {
      // Storage can be unavailable or full; the live conversation still works.
    }
  }, [agentSessionUuid, isHistoryReady, locationId, messages, storageKey]);

  useEffect(() => {
    threadRef.current?.scrollTo({
      top: threadRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, isReplying]);

  useEffect(() => {
    const inputElement = textInputRef.current;
    if (!inputElement) return;
    inputElement.style.height = 'auto';
    inputElement.style.height = `${Math.min(inputElement.scrollHeight, 96)}px`;
  }, [input]);

  /** Website telemetry for this demo (never message text). */
  const trackDemo = useCallback(
    (
      type: Parameters<typeof trackInteraction>[0],
      value: string | null = null,
      data: Record<string, unknown> = {}
    ) =>
      trackInteraction(type, value ?? businessName ?? null, {
        language,
        location_id: locationId,
        business: businessName ?? null,
        channel,
        ...data,
      }),
    [businessName, channel, language, locationId]
  );

  // First touch of the phone mock starts (or restores) the demo session.
  const engagedRef = useRef(false);
  const engage = () => {
    if (!engagedRef.current) {
      engagedRef.current = true;
      trackDemo('line_demo_session_started');
    }
    void ensureSession().catch(() => undefined);
  };

  useEffect(() => {
    if (!isHistoryReady || hasWelcomed || !sectionRef.current) return;

    const observer = new IntersectionObserver(
      entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect();
        trackDemo('line_demo_viewed');
        setIsReplying(true);
        pendingReply.current = window.setTimeout(() => {
          setMessages([greetingMessage(text, nextId)]);
          setHasWelcomed(true);
          setIsReplying(false);
          pendingReply.current = null;
        }, 1300);
      },
      { threshold: 0.3 }
    );

    observer.observe(sectionRef.current);
    return () => {
      observer.disconnect();
      if (pendingReply.current !== null) {
        window.clearTimeout(pendingReply.current);
        pendingReply.current = null;
      }
    };
  }, [hasWelcomed, isHistoryReady, text, trackDemo]);

  useEffect(
    () => () => {
      if (pendingReply.current !== null)
        window.clearTimeout(pendingReply.current);
      cameraStream.current?.getTracks().forEach(track => track.stop());
    },
    []
  );

  const sendMessage = async (
    value: string,
    source: 'typed' | 'suggested_prompt' | 'line_action' = 'typed'
  ) => {
    const message = value.trim();
    const images = pendingImages;
    if ((!message && images.length === 0) || isReplying || !hasWelcomed) return;
    trackDemo('line_demo_message_sent', source, { images: images.length });
    const sentAt = Date.now();

    setMessages(current => [
      ...current.map(chatMessage =>
        chatMessage.quickReplies
          ? { ...chatMessage, quickReplies: undefined }
          : chatMessage
      ),
      {
        id: nextId.current++,
        role: 'user',
        ...(message ? { text: message } : {}),
        ...(images.length
          ? {
              images: images.map(image => image.dataUrl),
              imageCount: images.length,
            }
          : {}),
      },
    ]);
    setInput('');
    setPendingImages([]);
    setCameraError('');
    setEmojiOpen(false);
    setIsReplying(true);
    try {
      const replies = await sendLineMessage(
        message,
        images.map(image => ({
          media_type: image.mediaType,
          data: image.data,
        }))
      );
      console.debug(`[${skin.name} demo] Reply received.`, {
        agentSessionUuid,
        messageCount: replies.length,
      });
      trackDemo('line_demo_reply_received', null, {
        latency_ms: Date.now() - sentAt,
        messages: replies.length,
      });
      setMessages(current => [
        ...current,
        ...chatMessagesFromLine(replies, nextId),
      ]);
    } catch (error) {
      console.error(`[${skin.name} demo] Reply failed.`, {
        agentSessionUuid,
        error,
      });
      trackDemo('line_demo_reply_failed', null, {
        latency_ms: Date.now() - sentAt,
        error: (error instanceof Error ? error.message : String(error)).slice(
          0,
          200
        ),
      });
      setMessages(current => [
        ...current,
        {
          id: nextId.current++,
          role: 'assistant',
          text: text.replyError,
        },
      ]);
    } finally {
      setIsReplying(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void sendMessage(input);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    void sendMessage(input);
  };

  const stageImageFiles = async (files: File[]) => {
    if (!hasWelcomed || isReplying || files.length === 0) return;
    setCameraError('');
    setEmojiOpen(false);
    const supportedFiles = files.filter(file => IMAGE_TYPES.has(file.type));
    const availableSlots = Math.max(0, MAX_IMAGES - pendingImages.length);
    let errorMessage = '';
    if (supportedFiles.length !== files.length) errorMessage = text.photoError;
    else if (supportedFiles.length > availableSlots)
      errorMessage = text.photoLimit;
    if (availableSlots === 0) {
      setCameraError(errorMessage || text.photoLimit);
      return;
    }

    const results = await Promise.allSettled(
      supportedFiles.slice(0, availableSlots).map(readImageFile)
    );
    const validCandidates = results
      .filter(
        (result): result is PromiseFulfilledResult<Omit<PendingImage, 'id'>> =>
          result.status === 'fulfilled'
      )
      .map(result => result.value);
    if (results.some(result => result.status === 'rejected')) {
      errorMessage = text.photoError;
    }
    let totalCharacters = pendingImages.reduce(
      (total, image) => total + image.data.length,
      0
    );
    const validImages = validCandidates
      .filter(image => {
        if (
          totalCharacters + image.data.length >
          MAX_IMAGE_TOTAL_BASE64_CHARS
        ) {
          errorMessage = text.photoTotalError;
          return false;
        }
        totalCharacters += image.data.length;
        return true;
      })
      .map(image => ({ id: nextImageId.current++, ...image }));
    setCameraError(errorMessage);
    if (validImages.length) {
      setPendingImages(current =>
        [...current, ...validImages].slice(0, MAX_IMAGES)
      );
      window.requestAnimationFrame(() => textInputRef.current?.focus());
    }
  };

  const stageCapturedImage = (dataUrl: string) => {
    if (pendingImages.length >= MAX_IMAGES) {
      setCameraError(text.photoLimit);
      return;
    }
    const image = imageFromDataUrl(
      dataUrl,
      `camera-${nextImageId.current}.jpg`
    );
    if (!image) {
      setCameraError(text.photoError);
      return;
    }
    const totalCharacters = pendingImages.reduce(
      (total, pendingImage) => total + pendingImage.data.length,
      image.data.length
    );
    if (totalCharacters > MAX_IMAGE_TOTAL_BASE64_CHARS) {
      setCameraError(text.photoTotalError);
      return;
    }
    setPendingImages(current => [
      ...current,
      { id: nextImageId.current++, ...image },
    ]);
    window.requestAnimationFrame(() => textInputRef.current?.focus());
  };

  const removePendingImage = (id: number) => {
    setPendingImages(current => current.filter(image => image.id !== id));
    setCameraError('');
  };

  const hasDraggedFiles = (event: DragEvent<HTMLElement>) =>
    Array.from(event.dataTransfer.types).includes('Files');

  const handleDragEnter = (event: DragEvent<HTMLElement>) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    if (!hasWelcomed || isReplying) return;
    dragDepth.current += 1;
    setIsDraggingImages(true);
  };

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect =
      hasWelcomed && !isReplying ? 'copy' : 'none';
  };

  const handleDragLeave = (event: DragEvent<HTMLElement>) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDraggingImages(false);
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    dragDepth.current = 0;
    setIsDraggingImages(false);
    if (!hasWelcomed || isReplying) return;
    void stageImageFiles(Array.from(event.dataTransfer.files));
  };

  const stopCamera = () => {
    cameraStream.current?.getTracks().forEach(track => track.stop());
    cameraStream.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOpen(false);
  };

  const startCamera = async () => {
    if (!hasWelcomed || isReplying) return;
    setEmojiOpen(false);
    setCameraError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(text.cameraError);
      return;
    }

    setCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      cameraStream.current = stream;
      await new Promise<void>(resolve =>
        window.requestAnimationFrame(() => resolve())
      );
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      stopCamera();
      setCameraError(text.cameraError);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video.videoHeight) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    const image = canvas.toDataURL('image/jpeg', 0.88);
    stopCamera();
    stageCapturedImage(image);
  };

  const handleLineAction = (action: ILineAction) => {
    if (action.type === 'uri' && action.uri) {
      trackDemo('line_demo_link_clicked', null, {
        url: action.uri.slice(0, 300),
      });
      window.open(action.uri, '_blank', 'noopener,noreferrer');
      return;
    }
    if (action.type === 'camera') {
      void startCamera();
      return;
    }
    if (action.type === 'cameraRoll') {
      fileInputRef.current?.click();
      return;
    }
    if (action.text) void sendMessage(action.text, 'line_action');
  };

  const reset = () => {
    trackDemo('line_demo_restarted');
    if (pendingReply.current !== null) {
      window.clearTimeout(pendingReply.current);
      pendingReply.current = null;
    }
    stopCamera();
    try {
      localStorage.removeItem(`${storageKey}:${locationId}`);
    } catch {
      // Storage can be unavailable; resetting the visible preview still works.
    }
    nextId.current = 1;
    nextImageId.current = 1;
    setMessages([]);
    setInput('');
    setPendingImages([]);
    setIsReplying(false);
    setIsDraggingImages(false);
    dragDepth.current = 0;
    setHasWelcomed(false);
    setEmojiOpen(false);
    setCameraError('');
  };

  return (
    <section
      ref={sectionRef}
      className={`${backgroundClasses} relative overflow-hidden py-20 lg:py-28`}
    >
      <WaveDivider
        position="top"
        backgroundColor="secondary"
        theme={skin.theme}
        emphasis
      />
      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-4 pt-8 sm:px-6 lg:grid-cols-[1.12fr_0.88fr] lg:px-8">
        <div>
          <div className="mb-5 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">
            <span
              className="rounded-full px-3 py-1.5"
              style={{ backgroundColor: `${c.accent}1a`, color: c.accent }}
            >
              {skin.name}
            </span>
            <span>{text.eyebrow}</span>
          </div>
          <h2 className="max-w-3xl text-4xl font-black tracking-[-0.035em] text-[var(--color-foreground)] sm:text-5xl lg:text-[3.4rem] lg:leading-[1.04]">
            {text.title}
          </h2>
          <p className="escape mt-6 max-w-2xl text-lg leading-8 text-[var(--color-foreground-secondary)]">
            {text.description}
          </p>

          <div className="mt-9 divide-y divide-[var(--color-border)] border-y border-[var(--color-border)]">
            {text.features.map(feature => (
              <div key={feature.title} className="flex gap-4 py-5">
                <span className="mt-0.5 h-7 w-7 shrink-0 text-[#3880ff]">
                  {featureIcon(feature.icon)}
                </span>
                <div>
                  <h3 className="text-lg font-bold text-[var(--color-foreground)]">
                    {feature.title}
                  </h3>
                  <p className="escape mt-1 text-sm leading-6 text-[var(--color-muted-foreground)] sm:text-base">
                    {feature.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <p className="escape mb-3 text-sm font-medium text-[var(--color-muted-foreground)]">
              {text.tryOne}
            </p>
            <div className="flex flex-wrap gap-2.5">
              {text.prompts.map(prompt => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => void sendMessage(prompt, 'suggested_prompt')}
                  disabled={isReplying || !hasWelcomed}
                  className="max-w-full shrink-0 break-all rounded-full border border-[var(--color-border-secondary)] bg-[var(--color-card-background)] px-4 py-2.5 text-left text-sm font-medium text-[var(--color-foreground)] shadow-sm transition hover:-translate-y-0.5 hover:border-[#3880ff] hover:text-[#2563eb] disabled:cursor-wait disabled:opacity-50"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div
          className="mx-auto w-full max-w-[430px]"
          onPointerDown={engage}
          onWheel={engage}
        >
          <div className="rounded-[3.2rem] bg-[#111214] p-[11px] shadow-[0_38px_90px_-34px_rgba(0,0,0,0.55)] ring-1 ring-black/10">
            <div
              className="relative flex h-[800px] max-h-[84vh] min-h-[700px] flex-col overflow-hidden rounded-[2.55rem]"
              style={{ backgroundColor: c.screen }}
              role="region"
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              aria-label={skin.copy[language].ariaPreview}
            >
              {isDraggingImages && (
                <div
                  className="pointer-events-none absolute inset-2 z-40 flex items-center justify-center rounded-[2.2rem] border-2 border-dashed bg-white/90 p-6 text-center text-sm font-bold shadow-inner backdrop-blur-sm"
                  style={{ borderColor: c.brand, color: c.accent }}
                >
                  {text.dropPhotos}
                </div>
              )}
              <div style={{ backgroundColor: c.brand, color: c.onBrand }}>
                <div className="flex items-center justify-between px-6 pb-1 pt-3 text-[11px] font-semibold">
                  <span>9:41</span>
                  <span
                    className="flex items-center gap-1.5"
                    aria-hidden="true"
                  >
                    <svg
                      viewBox="0 0 18 12"
                      className="h-3 w-[18px]"
                      fill="currentColor"
                    >
                      <rect x="1" y="8" width="2.5" height="3" rx="0.5" />
                      <rect x="5.5" y="6" width="2.5" height="5" rx="0.5" />
                      <rect x="10" y="3.5" width="2.5" height="7.5" rx="0.5" />
                      <rect x="14.5" y="1" width="2.5" height="10" rx="0.5" />
                    </svg>
                    <svg viewBox="0 0 16 12" className="h-3 w-4" fill="none">
                      <path
                        d="M2 4.2a9 9 0 0 1 12 0M4.3 6.7a5.7 5.7 0 0 1 7.4 0M6.7 9.2a2 2 0 0 1 2.6 0"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <svg
                      viewBox="0 0 22 11"
                      className="h-[11px] w-[22px]"
                      fill="none"
                    >
                      <rect
                        x="1"
                        y="1"
                        width="17"
                        height="9"
                        rx="2"
                        stroke="currentColor"
                        strokeWidth="1.3"
                      />
                      <path
                        d="M20 4v3"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <rect
                        x="3"
                        y="3"
                        width="13"
                        height="5"
                        rx="1"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                </div>
                <div className="flex min-w-0 items-center gap-3 px-5 pb-4 pt-2">
                  <Image
                    src="/assets/images/ai/obie-default.webp"
                    alt="Obie"
                    width={43}
                    height={43}
                    className="h-11 w-11 rounded-full border-2 border-white/80 object-cover"
                  />
                  <div className="min-w-0">
                    <div className="truncate text-base font-bold leading-tight">
                      {text.business}
                    </div>
                    <div className="truncate text-xs opacity-80">
                      {text.account}
                    </div>
                  </div>
                </div>
              </div>

              <div
                ref={threadRef}
                className="flex-1 space-y-3 overflow-y-auto px-4 py-5"
                aria-live="polite"
              >
                <div
                  className="mx-auto w-fit rounded-full px-3 py-1 text-xs font-medium"
                  style={{
                    backgroundColor: c.dateChip,
                    color: channel === 'line' ? '#ffffff' : '#3b4a54',
                  }}
                >
                  {text.today}
                </div>
                {messages.map(message => (
                  <div
                    key={message.id}
                    className={`flex w-full min-w-0 flex-col gap-2 ${message.role === 'user' ? 'items-end' : 'items-start'}`}
                  >
                    {message.flex ? (
                      <LineFlexPreview
                        contents={message.flex}
                        onAction={handleLineAction}
                        accent={c.accent}
                      />
                    ) : (
                      <div
                        className={`max-w-[86%] ${message.role === 'user' ? 'rounded-[1.3rem] rounded-tr-md' : 'rounded-[1.3rem] rounded-tl-md bg-white text-[#24272b]'} ${message.images?.length ? 'p-1.5' : 'px-4 py-3'} text-[14px] leading-[1.45rem] shadow-sm`}
                        style={
                          message.role === 'user'
                            ? {
                                backgroundColor: c.userBubble,
                                color: c.userText,
                              }
                            : undefined
                        }
                      >
                        {message.images?.length && (
                          <div
                            className={`grid gap-1.5 ${message.images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}
                          >
                            {message.images.map((image, index) => (
                              <Image
                                key={`${message.id}-image-${index}`}
                                src={image}
                                alt={text.photoAlt}
                                width={260}
                                height={195}
                                unoptimized
                                className={`${message.images!.length > 1 ? 'aspect-square' : 'max-h-56'} w-full rounded-[1rem] object-cover`}
                              />
                            ))}
                          </div>
                        )}
                        {(message.imageCount ?? 0) >
                          (message.images?.length ?? 0) && (
                          <div
                            className={`${message.images?.length ? 'px-2.5 pt-2' : ''} font-medium`}
                          >
                            📷{' '}
                            {photoCountLabel(
                              (message.imageCount ?? 0) -
                                (message.images?.length ?? 0),
                              language
                            )}
                          </div>
                        )}
                        {message.text && (
                          <div
                            className={
                              message.images?.length ? 'px-2.5 py-2' : ''
                            }
                          >
                            {message.text}
                          </div>
                        )}
                      </div>
                    )}
                    {message.quickReplies && (
                      <div className="flex w-full max-w-full gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {message.quickReplies.map(reply => (
                          <button
                            key={`${reply.label}-${reply.action.text || reply.action.uri || ''}`}
                            type="button"
                            onClick={() => handleLineAction(reply.action)}
                            className="shrink-0 rounded-full border border-white/90 bg-white/95 px-3.5 py-2 text-xs font-semibold shadow-sm transition hover:bg-white"
                            style={{ color: c.accent }}
                          >
                            {reply.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {isReplying && (
                  <div className="flex justify-start">
                    <div className="flex items-center gap-2 rounded-[1.3rem] rounded-tl-md bg-white px-4 py-3 text-xs text-[#697078] shadow-sm">
                      <span className="flex gap-1" aria-hidden="true">
                        <i className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#8a929a] [animation-delay:-0.2s]" />
                        <i className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#8a929a] [animation-delay:-0.1s]" />
                        <i className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#8a929a]" />
                      </span>
                      {hasWelcomed ? text.sending : text.typing}
                    </div>
                  </div>
                )}
              </div>

              {cameraError && (
                <div
                  className={`absolute left-3 right-3 z-20 rounded-xl bg-white px-3 py-2 text-center text-xs text-rose-600 shadow-lg ${pendingImages.length ? 'bottom-[150px]' : 'bottom-[70px]'}`}
                >
                  {cameraError}
                </div>
              )}

              {emojiOpen && (
                <div
                  className={`absolute left-3 right-3 z-20 rounded-[1.4rem] border border-black/5 bg-white/95 p-3 shadow-[0_14px_38px_rgba(30,42,54,0.2)] backdrop-blur ${pendingImages.length ? 'bottom-[150px]' : 'bottom-[70px]'}`}
                >
                  <div className="mb-2 flex items-center justify-between px-1">
                    <span className="text-[11px] font-semibold text-[#7b8289]">
                      {text.emojiRecent}
                    </span>
                    <button
                      type="button"
                      onClick={() => setEmojiOpen(false)}
                      className="flex h-6 w-6 items-center justify-center rounded-full text-base text-[#a0a6ac] hover:bg-[#f1f2f6]"
                      aria-label={
                        language === 'en'
                          ? 'Close emoji picker'
                          : '關閉表情符號'
                      }
                    >
                      ×
                    </button>
                  </div>
                  <div className="grid grid-cols-8 gap-1">
                    {emojis.map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        aria-label={emoji}
                        onClick={() =>
                          setInput(current => `${current}${emoji}`)
                        }
                        className="flex aspect-square items-center justify-center rounded-lg text-xl transition hover:bg-[#f1f2f6] hover:scale-110"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                className="flex items-end gap-1.5 px-3 py-3"
                style={{
                  backgroundColor: channel === 'line' ? '#ffffff' : '#f0f2f5',
                }}
              >
                <button
                  type="button"
                  disabled={
                    !hasWelcomed ||
                    isReplying ||
                    pendingImages.length >= MAX_IMAGES
                  }
                  onClick={startCamera}
                  title={text.camera}
                  aria-label={text.camera}
                  className="flex h-8 w-8 shrink-0 items-center justify-center text-[#737a82] hover:text-[#3f464d] disabled:opacity-40"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-6 w-6"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M4 8.5h3l1.5-2h7l1.5 2h3v10H4v-10Z"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="13.5"
                      r="3.2"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  disabled={
                    !hasWelcomed ||
                    isReplying ||
                    pendingImages.length >= MAX_IMAGES
                  }
                  onClick={() => {
                    setEmojiOpen(false);
                    fileInputRef.current?.click();
                  }}
                  title={text.photos}
                  aria-label={text.photos}
                  className="flex h-8 w-8 shrink-0 items-center justify-center text-[#737a82] hover:text-[#3f464d] disabled:opacity-40"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-6 w-6"
                    fill="none"
                    aria-hidden="true"
                  >
                    <rect
                      x="3.5"
                      y="4.5"
                      width="17"
                      height="15"
                      rx="2.3"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />
                    <circle cx="8.5" cy="9" r="1.5" fill="currentColor" />
                    <path
                      d="m5.5 17 4.2-4.2 3.1 2.7 2.4-2.3 3.3 3.8"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  multiple
                  className="hidden"
                  onChange={event => {
                    void stageImageFiles(Array.from(event.target.files ?? []));
                    event.target.value = '';
                  }}
                />
                <div
                  className="relative min-w-0 flex-1 rounded-[1.25rem] transition focus-within:ring-3"
                  style={{
                    backgroundColor: c.composer,
                    ['--tw-ring-color' as string]: `${c.brand}4d`,
                  }}
                >
                  {pendingImages.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto px-2.5 pb-1 pt-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      {pendingImages.map(image => (
                        <div
                          key={image.id}
                          className="relative h-[66px] w-[66px] shrink-0"
                        >
                          <Image
                            src={image.dataUrl}
                            alt={image.name || text.photoAlt}
                            width={66}
                            height={66}
                            unoptimized
                            className="h-full w-full rounded-xl border border-black/5 object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removePendingImage(image.id)}
                            aria-label={`${text.removePhoto}: ${image.name}`}
                            className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-white bg-[#34383d] text-sm leading-none text-white shadow-sm transition hover:bg-black"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <textarea
                    ref={textInputRef}
                    value={input}
                    onChange={event => setInput(event.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={text.placeholder}
                    aria-label={text.placeholder}
                    rows={1}
                    maxLength={200}
                    className="block max-h-24 min-h-10 w-full resize-none bg-transparent py-2.5 pl-3.5 pr-9 text-[14px] leading-5 text-[#2c3035] outline-none"
                  />
                  <button
                    type="button"
                    disabled={!hasWelcomed || isReplying}
                    onClick={() => {
                      setEmojiOpen(current => !current);
                    }}
                    title={text.emoji}
                    aria-label={text.emoji}
                    className="absolute bottom-1.5 right-1.5 flex h-7 w-7 items-center justify-center rounded-full text-[#9aa1a8] transition hover:bg-white hover:text-[#737b83] disabled:opacity-40"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="h-[19px] w-[19px]"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="8.5"
                        stroke="currentColor"
                        strokeWidth="1.45"
                      />
                      <circle cx="9" cy="10" r="0.85" fill="currentColor" />
                      <circle cx="15" cy="10" r="0.85" fill="currentColor" />
                      <path
                        d="M8.5 14c1 1.15 2.15 1.7 3.5 1.7s2.5-.55 3.5-1.7"
                        stroke="currentColor"
                        strokeWidth="1.35"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={
                    (!input.trim() && pendingImages.length === 0) ||
                    isReplying ||
                    !hasWelcomed
                  }
                  aria-label={language === 'en' ? 'Send message' : '發送訊息'}
                  className="flex h-9 w-9 shrink-0 items-center justify-center transition disabled:opacity-40"
                  style={{ color: c.accent }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-7 w-7"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3.8 4.2 21 11.5 3.8 19l2-6 9.2-1.5-9.2-1.4-2-5.9Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </form>

              {cameraOpen && (
                <div className="absolute inset-0 z-30 flex flex-col bg-black">
                  <button
                    type="button"
                    onClick={stopCamera}
                    aria-label={text.close}
                    className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-2xl text-white"
                  >
                    ×
                  </button>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={capturePhoto}
                    aria-label={text.capture}
                    className="absolute bottom-7 left-1/2 h-16 w-16 -translate-x-1/2 rounded-full border-4 border-white bg-white/30 ring-2 ring-black/20"
                  />
                </div>
              )}
            </div>
          </div>
          <p className="escape mx-auto mt-5 max-w-sm px-2 text-center text-xs leading-5 text-[var(--color-muted-foreground)]">
            {text.caption}
          </p>
          <button
            type="button"
            onClick={reset}
            className="mx-auto mt-2 block text-xs font-semibold hover:underline"
            style={{ color: c.accent }}
          >
            ↻ {text.resetPreview}
          </button>
        </div>
      </div>
      <WaveDivider
        position="bottom"
        backgroundColor="secondary"
        theme={skin.theme}
        emphasis
      />
    </section>
  );
}
